import { useRef } from "react";
import { gsap, useGSAP } from "@/lib/gsap";
import { usePrefersReducedMotion } from "@/lib/reduced-motion";
import { useInViewport } from "@/lib/use-in-viewport";

/**
 * 숫자 롤업. 화면에 처음 들어올 때 0에서 굴러 올라가고,
 * 그 뒤 값이 바뀌면 현재 숫자에서 이어서 짧게 움직여요.
 * prefers-reduced-motion 이면 애니메이션 없이 최종 값을 바로 보여줘요.
 */

export interface CountUpProps {
  value: number;
  /** 표시 형식. @default 천 단위 구분 정수 */
  format?: (v: number) => string;
  className?: string;
  /** 첫 등장 때 세는 시간(초). @default 0.9 */
  duration?: number;
}

const formatInteger = (v: number) => Math.round(v).toLocaleString("ko-KR");
/** 값이 바뀌었을 때 이어 붙는 짧은 시간(초). */
const FOLLOW_UP = 0.32;

export function CountUp({
  value,
  format = formatInteger,
  className = "",
  duration = 0.9,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = usePrefersReducedMotion();
  // 화면에 들어왔는지는 ScrollTrigger 가 아니라 IntersectionObserver 로 봐요 — 견적 하단 바처럼
  // position:fixed 조상 안에 있으면 스크롤 위치로 계산하는 트리거가 영영 안 걸려 숫자가 0 에 멈춰요.
  const inView = useInViewport(ref);
  // 값이 바뀌어 훅이 다시 돌 때, 0 이 아니라 화면에 떠 있던 숫자에서 이어 세요.
  const shown = useRef(0);
  const started = useRef(false);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      // 트윈 대상은 매번 새로 만드는 지역 객체예요 — revertOnUpdate 가 되돌려도 잃을 게 없어요.
      const proxy = { n: shown.current };
      const render = () => {
        shown.current = proxy.n;
        el.textContent = format(proxy.n);
      };

      if (reduce) {
        proxy.n = value;
        render();
        return;
      }
      render();

      // 첫 등장은 화면에 들어올 때까지 0 인 채로 기다려요. 한 번 센 뒤로는 바로 이어 붙고요.
      if (!inView && !started.current) return;

      gsap.to(proxy, {
        n: value,
        duration: started.current ? FOLLOW_UP : duration,
        ease: "power2.out",
        onUpdate: render,
        onStart: () => {
          started.current = true;
        },
      });
    },
    { dependencies: [value, inView, reduce, duration, format], scope: ref, revertOnUpdate: true }
  );

  // 숫자가 구르는 동안 자릿수 폭이 흔들리지 않게 tabular-nums 는 항상 붙여요.
  return <span ref={ref} className={`tabular-nums ${className}`} />;
}

export default CountUp;
